import React, { useEffect, useState, Suspense } from "react";
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Preload } from "@react-three/drei";
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { MeshStandardMaterial, Mesh, Color, Vector3, Box3 } from 'three';

const STLViewer = ({ url, currentColor, onStatsUpdate, data, currentDencity, mystl, isUpdateJob }) => {
  const [model, setModel] = useState(null);
  const [scale, setScale] = useState([1, 1, 1]);
  const [position, setPosition] = useState([0, 0, 0]);

  useEffect(() => {
    if (!url) return;

    const extension = url.split('.').pop().toLowerCase();
    const color = new Color(currentColor || 0xCCCCCC);

    const centerAndScale = (mesh, geometry) => {
      const boundingBox = new Box3().setFromObject(mesh);
      const center = new Vector3();
      boundingBox.getCenter(center);
      mesh.position.sub(center);

      const size = new Vector3();
      boundingBox.getSize(size);
      const maxAxis = Math.max(size.x, size.y, size.z);
      const scaleValue = 10 / maxAxis;

      setScale([scaleValue, scaleValue, scaleValue]);
      setPosition([
        -center.x * scaleValue,
        -center.y * scaleValue + 1.5,
        -center.z * scaleValue + 1
      ]);

      if (geometry && currentDencity > 0) {
        const volume = calculateVolume(geometry);
        const surfaceArea = calculateSurfaceArea(geometry);
        geometry.computeBoundingBox();
        const dimensions = geometry.boundingBox.getSize(new Vector3());
        const density = currentDencity || 1;
        const weight = volume * density;
        data.weight = weight;
        data.dimensions = dimensions;
        data.volume = volume;
        data.area = surfaceArea;
        onStatsUpdate({ ...data });
      }

      setModel(mesh);
    };

    if (extension === "stl") {
      const loader = new STLLoader();
      loader.load(url, (geometry) => {
        const material = new MeshStandardMaterial({
          color: color,
          roughness: 0.3,
          metalness: 0.9
        });
        const mesh = new Mesh(geometry, material);
        centerAndScale(mesh, geometry);
      });
    } else if (extension === "obj") {
      const loader = new OBJLoader();
      loader.load(url, (object) => {
        object.traverse((child) => {
          if (child instanceof Mesh) {
            child.material = new MeshStandardMaterial({
              color: color,
              roughness: 0.3,
              metalness: 0.9
            });
          }
        });
        centerAndScale(object, null); // No geometry to measure volume
      });
    }
  }, [url, currentColor]);

  const calculateVolume = (geometry) => {
    let volume = 0;
    const position = geometry.attributes.position.array;
    for (let i = 0; i < position.length; i += 9) {
      const p1 = new Vector3(position[i], position[i + 1], position[i + 2]);
      const p2 = new Vector3(position[i + 3], position[i + 4], position[i + 5]);
      const p3 = new Vector3(position[i + 6], position[i + 7], position[i + 8]);
      volume += signedVolumeOfTriangle(p1, p2, p3);
    }
    return Math.abs(volume);
  };

  const signedVolumeOfTriangle = (p1, p2, p3) => {
    return p1.dot(p2.cross(p3)) / 6.0;
  };

  const calculateSurfaceArea = (geometry) => {
    let area = 0;
    const position = geometry.attributes.position.array;
    for (let i = 0; i < position.length; i += 9) {
      const p1 = new Vector3(position[i], position[i + 1], position[i + 2]);
      const p2 = new Vector3(position[i + 3], position[i + 4], position[i + 5]);
      const p3 = new Vector3(position[i + 6], position[i + 7], position[i + 8]);
      area += triangleArea(p1, p2, p3);
    }
    return area;
  };

  const triangleArea = (p1, p2, p3) => {
    return p2.clone().sub(p1).cross(p3.clone().sub(p1)).length() / 2.0;
  };

  return (
    <div>
      <div style={{ width: '100%', height: "100%" }}>
        <Canvas
          frameloop='demand'
          shadows
          camera={{ position: [50, 50, 50], fov: 25 }}
          style={{
            width: '100%',
            height: `${mystl ? "100%" : "100vh"}`,
            overflow: 'hidden',
            padding: "1px",
            top: 0,
            left: 0,
            position: "absolute",
            boxSizing: 'border-box',
          }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={5} />
            <directionalLight position={[10, 10, 10]} intensity={1} castShadow />
            <directionalLight position={[-10, -10, -10]} intensity={0.5} castShadow />
            {model && (
              <primitive
                object={model}
                scale={scale}
                position={position}
                rotation={[4.7, 0, 0]}
              />
            )}
            <OrbitControls
              enableZoom={true}
              autoRotate={false}
              enablePan={false}
              target={[0, 0, 0]}
              maxPolarAngle={Math.PI / 2}
              minPolarAngle={0}
              maxAzimuthAngle={Infinity}
              minAzimuthAngle={-Infinity}
            />
            <Preload all />
          </Suspense>
        </Canvas>
      </div>
    </div>
  );
};

export default STLViewer;
